import { VBID, ShareableVBID } from '../types/vbid';

// 移除本地ShareableVBID定義，改為導入
// interface ShareableVBID {
//   mode: string;
//   path?: string;
//   timecode: string;
//   flag?: 'on' | 'off';
//   note?: string;
//   screenshot?: string;
//   key?: string;
//   privacy?: 'Public' | 'Private';
//   userId?: string;
// }

interface CompressedShareData {
  m: string;    // mode
  v: string;    // videoId
  t: {          // timecodes
    u: string[];  // unique timecodes
    r?: {        // repeated timecodes
      t: string;  // timecode
      k: number[];// keys
    }[];
  };
}

export const extractYouTubeVideoId = (input?: string | null): string => {
  if (!input) return '';
  try {
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
      return input;
    }
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /youtu\.be\/([a-zA-Z0-9_-]{11})/
    ];
    for (const pattern of patterns) {
      const match = input.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return '';
  } catch (error) {
    console.error('Error extracting YouTube video ID:', error);
    return '';
  }
};

/**
 * 將 YouTube 視頻 ID 轉換為完整 URL
 * 🔧 修復：處理雲端檔案和 GCS URL，不強制轉換為 YouTube URL
 */
const getFullYouTubeUrl = (videoId: string): string => {
  // 🔧 如果已經是完整 URL（GCS、Vimeo 等），直接返回
  if (videoId.startsWith('http://') || videoId.startsWith('https://')) {
    console.log('🔧 檢測到完整 URL，直接返回:', videoId.substring(0, 50) + '...');
    return videoId;
  }
  
  // 🔧 如果是檔案 ID（file_xxx），也直接返回
  if (videoId.startsWith('file_') || videoId.startsWith('gdrive_')) {
    console.log('🔧 檢測到雲端檔案 ID，直接返回:', videoId);
    return videoId;
  }
  
  // ✅ 只有 YouTube 影片 ID 才轉換為 YouTube URL
  return `https://www.youtube.com/watch?v=${videoId}`;
};

const normalizeYoutubeId = (candidate?: string | null): string => {
  if (!candidate) return '';
  if (/^[a-zA-Z0-9_-]{11}$/.test(candidate)) {
    return candidate;
  }
  const extracted = extractYouTubeVideoId(candidate);
  return extracted || '';
};

export const resolveShareYoutubeId = (video: any): string => {
  const candidates = [
    video?.youtubeInfo?.videoId,
    video?.youtubeInfo?.id,
    video?.originalVideoData?.youtubeInfo?.videoId,
    video?.originalVideoData?.youtubeInfo?.id,
    video?.videoInfo?.videoId,
    video?.videoInfo?.id,
    video?.path,
    video?.url,
    video?.videoUrl,
    video?.originalVideoData?.path,
    video?.originalVideoData?.url,
    video?.videoId,
    video?.id
  ];

  for (const candidate of candidates) {
    const normalized = normalizeYoutubeId(candidate);
    if (normalized) {
      return normalized;
    }
  }
  return '';
};

/**
 * 將 Video Mark 資訊編碼為 URL 安全的字符串
 * @param vbids Video Mark 資訊數組
 * @returns 編碼後的 URL 參數
 */
export const encodeVBIDData = (vbids: ShareableVBID[]): string => {
  try {
    if (vbids.length === 0) {
      throw new Error('沒有可分享的視頻標記');
    }

    // 提取共同的 videoId，移除 mode 屬性訪問
    const videoId = extractYouTubeVideoId(vbids[0].path || '');

    // 收集所有時間碼
    const timecodesMap = new Map<string, number[]>();
    vbids.forEach(vbid => {
      const [key, time] = vbid.timecode.split('_');
      if (!timecodesMap.has(time)) {
        timecodesMap.set(time, [parseInt(key)]);
      } else {
        timecodesMap.get(time)?.push(parseInt(key));
      }
    });

    // 分離唯一時間碼和重複時間碼
    const uniqueTimecodes: string[] = [];
    const repeatedTimecodes: { t: string; k: number[] }[] = [];

    timecodesMap.forEach((keys, time) => {
      if (keys.length === 1) {
        uniqueTimecodes.push(`${keys[0]}_${time}`);
      } else {
        repeatedTimecodes.push({ t: time, k: keys });
      }
    });

    // 構建壓縮數據，移除 mode 屬性
    const compressedData: CompressedShareData = {
      m: 'VIDEO', // 使用預設值
      v: videoId,
      t: {
        u: uniqueTimecodes,
        ...(repeatedTimecodes.length > 0 && { r: repeatedTimecodes })
      }
    };

    // 轉換為 JSON 字符串並進行 Base64 編碼
    const jsonString = JSON.stringify(compressedData);
    return btoa(jsonString);
  } catch (error) {
    console.error('Error encoding VBID data:', error);
    throw new Error('無法編碼視頻標記資訊');
  }
};

/**
 * 解碼 URL 中的 Video Mark 資訊
 * @param encodedData 編碼後的 URL 參數
 * @returns 解碼後的 Video Mark 資訊數組
 */
export const decodeVBIDData = (encodedData: string): ShareableVBID[] => {
  try {
    // Base64 解碼並解析 JSON
    const jsonString = atob(encodedData);
    const data = JSON.parse(jsonString) as CompressedShareData;
    
    const vbids: ShareableVBID[] = [];

    // 處理唯一時間碼
    data.t.u.forEach(timecode => {
      vbids.push({
        path: getFullYouTubeUrl(data.v),
        timecode: timecode,
        flag: 'on'
      } as ShareableVBID);
    });

    // 處理重複時間碼
    if (data.t.r) {
      data.t.r.forEach(repeat => {
        repeat.k.forEach(key => {
          vbids.push({
            path: getFullYouTubeUrl(data.v),
            timecode: `${key}_${repeat.t}`,
            flag: 'on'
          } as ShareableVBID);
        });
      });
    }

    return vbids;
  } catch (error) {
    console.error('Error decoding VBID data:', error);
    throw new Error('無法解碼視頻標記資訊');
  }
};

/**
 * 生成可分享的 URL
 * @param vbids Video Mark 資訊數組
 * @returns 完整的分享 URL
 */
export const generateShareUrl = (vbids: ShareableVBID[]): string => {
  try {
    const encodedData = encodeVBIDData(vbids);
    const baseUrl = window.location.origin.replace('www.', '');
    const shareUrl = `${baseUrl}/#/?vbid=${encodedData}`;
    
    return shareUrl;
  } catch (error) {
    console.error('Error generating share URL:', error);
    throw new Error('無法生成分享連結');
  }
}; 